import { useMemo, useCallback } from 'react';
import { valueFormat, getArraySort } from '@/pages/intelligence/utils/commonUtils';
import { buildTooltipFormatter } from '../../utils/common';

/* const ECHART_COLOR = [
  '#5086F3',
  '#5EC8F1',
  '#69D19C',
  '#F5D242',
  '#F17C86',
]; */

interface IUseOptionsPropsTypes extends PieEchartPropsTypes {
  isCN: boolean;
  containerWidth: number; // echart容器宽度
  tooltipHeaderLeftTitle?: string;
  tooltipHeaderRightTitle?: string;
  seriesNameFormatter?: (seriesName: string) => string;
}

const DEFAULT_PROPS: IUseOptionsPropsTypes = {
  isCN: false,
  data: null,
  ignoreSortNames: [],
  tooltipPosition: 'right',
  containerWidth: 0,
  tooltipHeaderLeftTitle: '',
  tooltipHeaderRightTitle: '',
  fontSize: 12,
};

const useOptions = (props: IUseOptionsPropsTypes) => {
  const {
    isCN,
    data,
    ignoreSortNames,
    seriesNameFormatter,
    tooltipPosition,
    containerWidth,
    tooltipHeaderLeftTitle,
    tooltipHeaderRightTitle,
    fontSize,
  } = props;

  const dataForTooltip = useMemo(() => {
    const list = data?.data;
    if (!Array.isArray(list)) return [];
    return list.map(item => ({ data: item, value: item?.value, color: item?.color }));
  }, [data]);

  const tooltipFormatter = useMemo(() => buildTooltipFormatter({
    isCN,
    headerLeftTitle: tooltipHeaderLeftTitle,
    headerRightTitle: tooltipHeaderRightTitle,
    seriesNameFormatter,
  }), [isCN, tooltipHeaderLeftTitle, tooltipHeaderRightTitle, seriesNameFormatter]);

  // 改变引导线末端（第3个点）的位置，使得引导线穿过文案，以及文案内容往上移
  const getLabelLayout = useCallback(
    (params: any) => {
      const isLeft = params.labelRect.x < containerWidth / 2;
      const points = params.labelLinePoints;
      if (!points) return {};
      points[2][0] = isLeft ? params.labelRect.x : params.labelRect.x + params.labelRect.width;
      return {
        dy: -3, // 文案内容往上移
        labelLinePoints: points,
        // moveOverlap: 'shiftY',
        // hideOverlap: true,
      };
    },
    [containerWidth],
  );

  // 对数据进行降序排序
  const sortData = useMemo(() => {
    let newData = data?.data;
    if (!Array.isArray(newData) || !newData?.length) return { ...data, total: 0, data: [] };
    let total = 0;
    if (Array.isArray(ignoreSortNames) && ignoreSortNames?.length) {
      const ignoreSortData = [];
      const needSortData = [];
      for (let j = 0; j < newData?.length; j++) {
        const k = newData?.[j];
        const name = newData?.[j]?.name;
        if (ignoreSortNames?.includes(name || '')) {
          ignoreSortData.push(k);
        } else {
          needSortData.push(k);
        }
      }
      const sortData = getArraySort(needSortData, 'number', 'desc', 'value');
      newData = [...sortData, ...ignoreSortData];
    } else {
      newData = getArraySort(newData, 'number', 'desc', 'value');
    }
    total = newData?.reduce((o, t) => {
      o = o + (Number(t?.value) || 0);
      return o;
    }, 0);

    return { ...data, total, data: newData };
  }, [data, ignoreSortNames]);

  const options = useMemo(() => {
    const data = sortData?.data;
    // 通过最长的name计算length2的长度，以确保引导线内容不会出现在饼环上
    let maxName = '';
    data?.forEach((t) => {
      if (t?.name?.length > maxName?.length) maxName = `${valueFormat(t?.name)}`;
    });
    // 格式是xx.xxB/M/K(xx%)
    const length2 = maxName?.length;
    const seriesData = data?.map((t) => ({
      ...t,
      itemStyle: {
        color: t?.color,
        borderRadius: 5,
      },
    }));
    const opt = {
      textStyle: {
        fontSize,
      },
      // color: ECHART_COLOR,
      grid: {
        containLabel: true,
      },
      tooltip: {
        // show: true,
        // showContent: true,
        // alwaysShowContent: true,
        className: 'dbi-common-echarts-tooltip-primary-styles',
        trigger: 'item',
        showDelay: 0,
        hideDelay: 0,
        // transitionDuration: 0.2,
        transitionDuration: 0,
        enterable: false,
        borderColor: 'transparent',
        // position: function (point: [number, number], params: any, dom: any, rect: any, size: any) { // 浮窗位置
        //   const { dataIndex } = params, { contentSize } = size;
        //   const verticalOffset = -(contentSize?.[1] / 2);
        //   const position = tooltipPosition === 'left'
        //     ? [point?.[0] - contentSize?.[0] - 20, point?.[1] + verticalOffset]
        //     : [point?.[0] + 20, point?.[1] + verticalOffset];
        //   return position;
        // },
        textStyle: {
          fontSize,
        },
        formatter: () => tooltipFormatter(dataForTooltip),
        backgroundColor: 'rgba(0,0,0,0)'
      },
      title: {
        text: (isCN ? sortData?.themeCN : sortData?.theme) || sortData?.theme,
        left: '49%',
        top: '50%',
        textAlign: 'center',
        textVerticalAlign: 'middle',
        textStyle: {
          width: 76,
          height: 50,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 18,
          color: '#262832',
          overflow: 'break',
        },
      },
      series: [
        {
          type: 'pie',
          top: 8, // 确保引导线末端的文案不会被遮掉
          minAngle: 5,
          radius: ['45px', '65px'], // 圆环形状(内层半径，外层半径)
          center: ['50%', '50%'], // 圆环位置
          data: seriesData,
          // 间距
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
          },
          // 调整角度
          // startAngle: 180,
          startAngle: 90,
          label: {
            show: true,
            position: 'outer',
            alignTo: 'edge',
            formatter: (params: any) => {
              const { name, value, percent } = params || {};
              return `${valueFormat(value)}(${Math?.round(percent) || 0}%)`;
            },
            color: '#262832',
            fontSize: 12,
            // lineHeight: 16,
            // padding: [-18, 0],
            // overflow: 'none',
            // minMargin: 5,
            edgeDistance: 10,
            distanceToLabelLine: 1,
            bleedMargin: 0,
            // rich: {
            //   name: {
            //     color: '#262832',
            //     padding: [0, 0, 6, 0],
            //   },
            // },
          },
          labelLine: {
            // 折线弯曲程度配置
            length: 10,
            // 通过计算保证第二段长度，文字不会出现...
            length2,
            showAbove: false,
            // minTurnAngle: 80,
            maxSurfaceAngle: 80,
            lineStyle: {
              width: 2,
            },
          },
          labelLayout: getLabelLayout,
        },
      ],
    };

    return opt;
  }, [isCN, sortData, tooltipPosition, containerWidth, seriesNameFormatter, dataForTooltip, tooltipFormatter]);

  return {
    options,
  };
};

useOptions.defaultProps = DEFAULT_PROPS;

export default useOptions;
